import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'admin' | 'staff' | 'user' | null;

export const useUserRole = () => {
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
  });

  const { data: roles, isLoading: rolesLoading, error } = useQuery({
    queryKey: ['user-roles', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);
      
      if (error) throw error;
      return data?.map(r => r.role as UserRole) || [];
    },
    enabled: !!session?.user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const hasRole = (role: UserRole): boolean => {
    if (!roles || roles.length === 0) return false;
    return roles.includes(role);
  };

  const isAdmin = hasRole('admin');
  const isStaff = hasRole('staff');
  const isAdminOrStaff = isAdmin || isStaff;

  return {
    roles: roles || [],
    isAdmin,
    isStaff,
    isAdminOrStaff,
    hasRole,
    isLoading: sessionLoading || rolesLoading,
    error,
  };
};
